import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import ClientsCarousel from '../../components/Carousel/ClientsCarousel';
import { 
  LuStar, 
  LuArrowRight, 
  LuCircleCheck, 
  LuUsers, 
  LuBuilding, 
  LuGlobe, 
  LuShieldCheck,
  LuHistory,
  LuTarget,
  LuAward
} from 'react-icons/lu';

// Import des images - Vous devrez créer ou obtenir ces images
import AboutImage from '../../assets/logo.png'; // Image principale

function About() {
  const { t } = useTranslation();
  const links = [
    { key: "about.links.why", href: "#why-choose-us" },
    { key: "about.links.contact", href: "/contact" },
  ];

  const stats = [
    { key: "about.stats.companies", value: "1200+", icon: <LuBuilding className="w-8 h-8" /> },
    { key: "about.stats.employees", value: "50 000+", icon: <LuUsers className="w-8 h-8" /> },
    { key: "about.stats.countries", value: "4", icon: <LuGlobe className="w-8 h-8" /> },
    { key: "about.stats.compliance", value: "100%", icon: <LuShieldCheck className="w-8 h-8" /> },
  ];


  const values = [
    {
      title: t('about.values.innovation_title'),
      description: t('about.values.innovation_desc'),
      icon: <LuTarget className="w-6 h-6" />,
      color: "bg-gradient-to-br from-orange-50 to-orange-100",
      iconColor: "text-orange-600",
      accentColor: "border-orange-200"
    },
    {
      title: t('about.values.reliability_title'),
      description: t('about.values.reliability_desc'),
      icon: <LuShieldCheck className="w-6 h-6" />,
      color: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      iconColor: "text-emerald-600",
      accentColor: "border-emerald-200"
    },
    {
      title: t('about.values.excellence_title'),
      description: t('about.values.excellence_desc'),
      icon: <LuAward className="w-6 h-6" />,
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      iconColor: "text-blue-600",
      accentColor: "border-blue-200"
    }
  ];

  const benefits = t('about.benefits', { returnObjects: true });

  const clients = t('about.clients', { returnObjects: true });


  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 overflow-hidden -z-10">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-yellow-200/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full blur-3xl"></div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-orange-200/50 rounded-full px-4 py-2 mb-4 shadow-lg">
              <LuStar className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-medium text-gray-700">{t('about.badge')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('about.heading')}{" "}
              <span className="bg-gradient-to-r from-orange-500 to-yellow-500 bg-clip-text text-transparent">
                {t('about.heading_highlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.subtitle')}
            </p>

            {/* Liens utiles */}
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-base font-medium">
              {links.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  className="px-5 py-2.5 rounded-lg bg-white shadow-sm border border-orange-200 hover:bg-orange-50 transition-colors text-gray-800 hover:text-orange-600 flex items-center gap-1"
                >
                  {t(link.key)}
                  <LuArrowRight className="w-4 h-4" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Statistiques */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12 max-w-5xl mx-auto"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.key}
                variants={itemVariants}
                className="bg-white rounded-2xl p-6 shadow-lg border border-orange-100 hover:shadow-xl transition-all"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="p-3 rounded-full bg-gradient-to-r from-orange-50 to-orange-100 text-orange-500 mb-4">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-600">{t(stat.key)}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Notre Histoire */}
      <section id="history" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="flex flex-col lg:flex-row gap-12 items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            {/* Image */}
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-r from-orange-500/30 to-yellow-500/30 blur-lg"></div>
                <img
                  src={AboutImage}
                  alt={t('about.image_alt')}
                  className="relative rounded-[2rem] shadow-xl object-cover w-full h-full"
                  onError={(e) => {
                    e.target.src = "https://images.pexels.com/photos/3184460/pexels-photo-3184460.jpeg?auto=compress&cs=tinysrgb&w=1600";
                  }}
                />
              </div>
            </div>
            
            {/* Content */}
            <motion.div 
              className="lg:w-1/2"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.div variants={itemVariants} className="flex items-center gap-3 mb-5">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                  <LuHistory className="w-6 h-6" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">{t('about.history_heading')}</h2>
              </motion.div>

              <motion.p variants={itemVariants} className="text-gray-600 mb-6">
                <Trans
                  i18nKey="about.history_p1"
                  components={{ strong: <strong className="font-semibold text-gray-900" /> }}
                />
              </motion.p>

              <motion.p variants={itemVariants} className="text-gray-600 mb-6">
                {t('about.history_p2')}
              </motion.p>

              <motion.p variants={itemVariants} className="text-gray-600 mb-6">
                {t('about.history_p3')}
              </motion.p>

              <motion.div variants={itemVariants}>
                <a
                  href="#why-choose-us"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl text-white font-medium shadow-md hover:shadow-lg transition-shadow"
                >
                  {t('about.history_cta')}
                  <LuArrowRight className="w-5 h-5" />
                </a>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Nos Valeurs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('about.values_heading')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.values_subtitle')}
            </p>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`${value.color} rounded-2xl p-8 border ${value.accentColor} shadow-lg hover:shadow-xl transition-shadow`}
              >
                <div className={`p-3 rounded-xl bg-white/80 ${value.iconColor} shadow-sm inline-block mb-6`}>
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-700">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section id="why-choose-us" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('about.why_heading')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.why_subtitle')}
            </p>
          </motion.div>

          <div className="flex flex-col lg:flex-row gap-12 items-center">
            {/* Benefits list */}
            <motion.div
              className="lg:w-1/2 bg-white rounded-2xl p-8 shadow-xl"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('about.benefits_heading')}</h3>
              
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div 
                    key={index}
                    variants={itemVariants}
                    className="flex items-start gap-3"
                  >
                    <div className="p-1 bg-orange-100 rounded-full text-orange-600 mt-1 flex-shrink-0">
                      <LuCircleCheck className="w-5 h-5" />
                    </div>
                    <p className="text-gray-700">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            {/* Clients list */}
            <motion.div 
              className="lg:w-1/2 bg-white rounded-2xl p-8 shadow-xl"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('about.clients_heading')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {clients.map((client, index) => (
                  <motion.div 
                    key={index}
                    variants={itemVariants}
                    className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200"
                  >
                    <p className="text-gray-700">{client}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Nos partenaires */}
      <section className="py-16 bg-white">
        <div className="text-center mb-10 px-4">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">{t('about.partners_heading')}</h2>
          <p className="text-2xl text-slate-600 max-w-5xl mx-auto leading-snug">
            {t('about.partners_subtitle')}
          </p>
        </div>
        <ClientsCarousel />
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="bg-gradient-to-r from-orange-500 to-yellow-500 rounded-3xl shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="p-12 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">{t('about.cta_heading')}</h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto">
                {t('about.cta_subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/demo"
                  className="px-8 py-4 bg-white text-orange-600 font-bold rounded-xl shadow-md hover:shadow-lg transition-shadow"
                >
                  {t('about.cta_demo')}
                </a>
                <a
                  href="/contact"
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white font-bold rounded-xl shadow-md hover:bg-white/30 transition-colors"
                >
                  {t('about.cta_contact')}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}

export default About;


